import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Card, CardHeader, CardTitle, CardDescription, CardContent,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Users, Plus, Pencil, Trash2, Building, User, Search, Eye, UserSquare2, MapPin, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import EmptyState from '@/components/common/EmptyState';
import {
  useCustomers, useCreateCustomer, useUpdateCustomer, useDeleteCustomer,
  type Customer, type CustomerType,
} from '@/hooks/useCustomers';
import { useCurrentOrganization } from '@/contexts/OrganizationContext';
import { useAuth } from '@/contexts/AuthContext';
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar';
import AppSidebar from '@/components/layout/AppSidebar';
import Header from '@/components/layout/Header';
import { usePlanUsage } from '@/hooks/usePlanUsage';
import { UpsellTooltip, WriteBlockedTooltip } from '@/components/billing/UpsellTooltip';
import { useCanWrite } from '@/hooks/useCanWrite';
import CustomersMapInline, {
  DEFAULT_STATUS_FILTER,
  type StatusFilter,
} from '@/components/customers/CustomersMapInline';
import { useCustomersMap } from '@/hooks/useCustomersMap';
import { STATUS_ORDER } from '@/utils/map/pieIcon';
import { cn } from '@/lib/utils';

interface EditableCustomerForm {
  id?: string;
  name: string;
  type: CustomerType;
  vat_number: string;
  fiscal_code: string;
  address_line: string;
  city: string;
  zip_code: string;
  province: string;
  phone: string;
  email: string;
  pec: string;
  sdi_code: string;
  notes: string;
}

const emptyForm: EditableCustomerForm = {
  name: '', type: 'individual',
  vat_number: '', fiscal_code: '',
  address_line: '', city: '', zip_code: '', province: '',
  phone: '', email: '', pec: '', sdi_code: '',
  notes: '',
};

function customerToForm(c: Customer): EditableCustomerForm {
  return {
    id: c.id,
    name: c.name ?? '',
    type: c.type,
    vat_number: c.vat_number ?? '',
    fiscal_code: c.fiscal_code ?? '',
    address_line: c.address_line ?? '',
    city: c.city ?? '',
    zip_code: c.zip_code ?? '',
    province: c.province ?? '',
    phone: c.phone ?? '',
    email: c.email ?? '',
    pec: c.pec ?? '',
    sdi_code: c.sdi_code ?? '',
    notes: c.notes ?? '',
  };
}

/**
 * Anagrafica clienti dell'organizzazione (F24).
 * - Lista con search
 * - Dialog crea/modifica
 * - Persona fisica (codice fiscale) vs Azienda (P.IVA + SDI/PEC)
 * - Eliminazione con conferma
 */
const Customers: React.FC = () => {
  const { currentRole, currentOrganization } = useCurrentOrganization();
  const { user } = useAuth();
  const canManage = ['owner', 'admin', 'supervisor', 'technician'].includes(currentRole ?? '');
  const canDelete = ['owner', 'admin', 'supervisor'].includes(currentRole ?? '');
  const isTechnician = currentRole === 'technician';
  // Scope TEC: modifica solo i propri record (created_by = auth.uid()).
  // I ruoli senior modificano TUTTO.
  const canEditRecord = (c: Customer): boolean => {
    if (!canManage) return false;
    if (!isTechnician) return true; // owner/admin/supervisor modificano tutto
    return c.created_by === user?.id; // technician solo i propri
  };
  const { customers: customersUsage } = usePlanUsage();
  const { canWrite } = useCanWrite();

  const { data: customers = [], isLoading } = useCustomers();
  const { data: mapPoints = [] } = useCustomersMap();
  const createCustomer = useCreateCustomer();
  const updateCustomer = useUpdateCustomer();
  const deleteCustomer = useDeleteCustomer();

  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<EditableCustomerForm>(emptyForm);
  // F20 — selected row sync con mappa inline (click riga ↔ centra pin)
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  // Filtro stati preventivo condiviso tra mappa e lista: i 4 checkbox sulla
  // mappa filtrano anche la tabella clienti a sinistra. Un cliente è visibile
  // se ha almeno € 1 in uno degli stati attivi.
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(DEFAULT_STATUS_FILTER);

  // Ordinamento colonne tabella
  type SortKey = 'type' | 'name' | 'piva' | 'city' | 'contacts';
  type SortDir = 'asc' | 'desc';
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>({ key: 'name', dir: 'asc' });
  const toggleSort = (key: SortKey) => {
    setSort(prev =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'asc' },
    );
  };

  // Index amount-per-cliente da useCustomersMap per il filtro lista
  const byStatusByCustomer = useMemo(() => {
    const m = new Map<string, typeof mapPoints[number]['byStatus']>();
    for (const p of mapPoints) {
      m.set(p.id, p.byStatus);
    }
    return m;
  }, [mapPoints]);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const activeStatuses = STATUS_ORDER.filter(s => statusFilter[s]);
    const allOff = activeStatuses.length === 0;
    const allOn = activeStatuses.length === STATUS_ORDER.length;
    let list = customers;
    // Filtro stati: applico solo se la selezione non è "tutti attivi" (full set)
    // né "nessuno attivo" (in quel caso lista vuota, coerente con mappa vuota).
    if (allOff) {
      list = [];
    } else if (!allOn) {
      list = list.filter(c => {
        const bs = byStatusByCustomer.get(c.id);
        if (!bs) return false;
        return activeStatuses.some(s => bs[s].amount > 0);
      });
    }
    if (q) {
      list = list.filter(c =>
        [c.name, c.vat_number, c.fiscal_code, c.city, c.email, c.phone]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q))
      );
    }
    // Ordinamento per la colonna selezionata
    const dir = sort.dir === 'asc' ? 1 : -1;
    const getKey = (c: Customer): string => {
      switch (sort.key) {
        case 'type':     return c.type === 'company' ? 'azienda' : 'privato';
        case 'name':     return (c.name ?? '').toLowerCase();
        case 'piva':     return (c.vat_number || c.fiscal_code || '').toLowerCase();
        case 'city':     return (c.city ?? '').toLowerCase();
        case 'contacts': return (c.email || c.phone || '').toLowerCase();
        default:         return '';
      }
    };
    return [...list].sort((a, b) => {
      const ka = getKey(a), kb = getKey(b);
      // Stringa vuota va sempre in fondo, sia asc che desc
      if (ka === '' && kb === '') return 0;
      if (ka === '') return 1;
      if (kb === '') return -1;
      return ka.localeCompare(kb, 'it') * dir;
    });
  }, [customers, searchQuery, statusFilter, byStatusByCustomer, sort]);

  // Header sortabile con freccia direzionale
  const SortHeader: React.FC<{ label: string; sortKey: SortKey; className?: string }> = ({
    label, sortKey, className,
  }) => {
    const active = sort.key === sortKey;
    const Icon = !active ? ArrowUpDown : sort.dir === 'asc' ? ArrowUp : ArrowDown;
    return (
      <button
        type="button"
        onClick={() => toggleSort(sortKey)}
        className={cn(
          'inline-flex items-center gap-1 hover:text-foreground transition-colors',
          active && 'text-foreground font-semibold',
          className,
        )}
      >
        <span>{label}</span>
        <Icon className={cn('h-3 w-3', !active && 'opacity-40')} />
      </button>
    );
  };

  const openNew = () => {
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (c: Customer) => {
    setForm(customerToForm(c));
    setDialogOpen(true);
  };

  const handleSubmit = () => {
    if (!form.name.trim()) return;
    const payload = {
      name: form.name.trim(),
      type: form.type,
      vat_number: form.vat_number.trim(),
      fiscal_code: form.fiscal_code.trim().toUpperCase(),
      address_line: form.address_line.trim(),
      city: form.city.trim(),
      zip_code: form.zip_code.trim(),
      province: form.province.trim().toUpperCase().slice(0, 2),
      phone: form.phone.trim(),
      email: form.email.trim(),
      pec: form.pec.trim(),
      sdi_code: form.sdi_code.trim().toUpperCase(),
      notes: form.notes.trim(),
    };
    if (form.id) {
      updateCustomer.mutate({ id: form.id, ...payload }, {
        onSuccess: () => setDialogOpen(false),
      });
    } else {
      createCustomer.mutate(payload, {
        onSuccess: () => setDialogOpen(false),
      });
    }
  };

  const handleDelete = (c: Customer) => {
    if (!confirm(`Eliminare il cliente "${c.name}"?\n\nI progetti associati resteranno ma senza riferimento al cliente.`)) return;
    deleteCustomer.mutate(c.id);
  };

  if (!currentOrganization) {
    return (
      <SidebarProvider>
        <div className="min-h-screen flex w-full">
          <AppSidebar />
          <SidebarInset className="flex flex-col">
            <Header />
            <main className="flex-1 p-6 md:p-8">
              <p className="text-muted-foreground">Nessuna organizzazione attiva.</p>
            </main>
          </SidebarInset>
        </div>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <AppSidebar />
        <SidebarInset className="flex flex-col">
          <Header />
          <main className="flex-1 p-6 md:p-8 space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Users className="h-6 w-6 text-primary" />
          <div>
            <h1 className="text-2xl font-bold tracking-tight" data-tour-id="customers-page">Clienti</h1>
            <p className="text-muted-foreground text-sm">
              Anagrafica clienti dell'organizzazione. Associabili ai progetti per intestazione
              automatica dei preventivi.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/customers/map">
            <Button variant="outline">
              <MapPin className="h-4 w-4 mr-2" />
              Mappa
            </Button>
          </Link>
          {canManage && (
            !canWrite ? (
              <WriteBlockedTooltip action="Crea nuovo cliente">
                <span>
                  <Button disabled>
                    <Plus className="h-4 w-4 mr-2" />
                    Nuovo cliente
                  </Button>
                </span>
              </WriteBlockedTooltip>
            ) : customersUsage.atLimit ? (
              <UpsellTooltip plan="studio" feature={`Limite ${customersUsage.max} clienti raggiunto`}>
                <span>
                  <Button disabled>
                    <Plus className="h-4 w-4 mr-2" />
                    Nuovo cliente
                    <span className="ml-2 text-xs font-mono opacity-80">({customersUsage.label})</span>
                  </Button>
                </span>
              </UpsellTooltip>
            ) : (
              <Button onClick={openNew}>
                <Plus className="h-4 w-4 mr-2" />
                Nuovo cliente
                {!customersUsage.unlimited && (
                  <span className="ml-2 text-xs font-mono opacity-80">({customersUsage.label})</span>
                )}
              </Button>
            )
          )}
        </div>
      </div>

      {/* F20 — Split list+map: lista a sx, mappa inline a dx su xl ≥1280px */}
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_560px] gap-6 items-start">
      <Card className="min-w-0">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground font-normal">
              {filtered.length} {filtered.length === 1 ? 'cliente' : 'clienti'}
            </span>
            <div className="relative w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Cerca per nome, P.IVA, città…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-muted-foreground text-sm">Caricamento…</p>
          ) : filtered.length === 0 ? (
            customers.length === 0 ? (
              <EmptyState
                icon={<UserSquare2 className="h-7 w-7" />}
                title="Crea il primo cliente"
                description="L'anagrafica clienti ti permette di pre-compilare i preventivi, raggruppare i progetti e tracciare lo storico per cliente."
                action={canManage ? (
                  <Button onClick={openNew}>
                    <Plus className="h-4 w-4 mr-2" />
                    Crea il primo cliente
                  </Button>
                ) : undefined}
                secondaryAction={!canManage ? 'Solo owner/admin/supervisor possono creare clienti.' : undefined}
              />
            ) : (
              <EmptyState
                icon={<Users className="h-7 w-7" />}
                title="Nessun cliente corrisponde alla ricerca"
                description="Prova a modificare i termini di ricerca."
              />
            )
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead><SortHeader label="Tipo" sortKey="type" /></TableHead>
                  <TableHead><SortHeader label="Nome / Ragione sociale" sortKey="name" /></TableHead>
                  {/* P.IVA nascosta su xl per dare spazio quando la mappa è inline */}
                  <TableHead className="hidden 2xl:table-cell">
                    <SortHeader label="P.IVA / CF" sortKey="piva" />
                  </TableHead>
                  <TableHead><SortHeader label="Città" sortKey="city" /></TableHead>
                  <TableHead className="hidden xl:table-cell 2xl:table-cell">
                    <SortHeader label="Contatti" sortKey="contacts" />
                  </TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(c => (
                  <TableRow
                    key={c.id}
                    onClick={() => setSelectedCustomerId(c.id)}
                    className={cn(
                      'cursor-pointer',
                      selectedCustomerId === c.id && 'bg-primary/5 hover:bg-primary/10',
                    )}
                  >
                    <TableCell>
                      {c.type === 'company' ? (
                        <Badge variant="secondary" className="gap-1">
                          <Building className="h-3 w-3" />
                          Azienda
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="gap-1">
                          <User className="h-3 w-3" />
                          Privato
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="font-medium">
                      <Link to={`/customers/${c.id}`} className="hover:underline hover:text-primary">
                        {c.name}
                      </Link>
                    </TableCell>
                    <TableCell className="hidden 2xl:table-cell text-sm text-muted-foreground">
                      {c.vat_number || c.fiscal_code || '—'}
                    </TableCell>
                    <TableCell className="text-sm">
                      {c.city ? `${c.city}${c.province ? ` (${c.province})` : ''}` : '—'}
                    </TableCell>
                    <TableCell className="hidden xl:table-cell 2xl:table-cell text-xs text-muted-foreground">
                      {c.email || c.phone ? (
                        <div className="flex flex-col leading-tight gap-0.5">
                          {c.email && <span className="truncate max-w-[180px]" title={c.email}>{c.email}</span>}
                          {c.phone && <span className="font-mono text-[11px]">{c.phone}</span>}
                        </div>
                      ) : '—'}
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button variant="ghost" size="icon" asChild title="Visualizza">
                        <Link to={`/customers/${c.id}`}>
                          <Eye className="h-4 w-4" />
                        </Link>
                      </Button>
                      {canEditRecord(c) && (
                        <Button variant="ghost" size="icon" onClick={() => openEdit(c)} title="Modifica">
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                      {canDelete && (
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(c)} title="Elimina">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Mappa inline — visibile solo su xl ≥1280px. Sotto: bottone "Mappa" nell'header naviga a /customers/map */}
      <aside className="hidden xl:block sticky top-6 self-start">
        <CustomersMapInline
          visibleIds={new Set(filtered.map(c => c.id))}
          selectedId={selectedCustomerId}
          onSelect={setSelectedCustomerId}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
        />
      </aside>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{form.id ? 'Modifica cliente' : 'Nuovo cliente'}</DialogTitle>
            <DialogDescription>
              {form.type === 'company'
                ? 'Azienda: usa P.IVA e codice SDI per fatturazione elettronica.'
                : 'Persona fisica: usa codice fiscale per fatturazione.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="cu-type">Tipo</Label>
                <select
                  id="cu-type"
                  value={form.type}
                  onChange={(e) => setForm(f => ({ ...f, type: e.target.value as CustomerType }))}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="individual">Persona fisica</option>
                  <option value="company">Azienda / Studio</option>
                </select>
              </div>
              <div>
                <Label htmlFor="cu-name">{form.type === 'company' ? 'Ragione sociale *' : 'Nome e cognome *'}</Label>
                <Input
                  id="cu-name"
                  value={form.name}
                  onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                  required
                />
              </div>
              {form.type === 'company' ? (
                <>
                  <div>
                    <Label htmlFor="cu-vat">Partita IVA</Label>
                    <Input
                      id="cu-vat"
                      value={form.vat_number}
                      onChange={(e) => setForm(f => ({ ...f, vat_number: e.target.value }))}
                      placeholder="IT12345678901"
                    />
                  </div>
                  <div>
                    <Label htmlFor="cu-sdi">Codice SDI</Label>
                    <Input
                      id="cu-sdi"
                      value={form.sdi_code}
                      onChange={(e) => setForm(f => ({ ...f, sdi_code: e.target.value }))}
                      placeholder="0000000"
                      maxLength={7}
                    />
                  </div>
                </>
              ) : (
                <div className="md:col-span-2">
                  <Label htmlFor="cu-cf">Codice Fiscale</Label>
                  <Input
                    id="cu-cf"
                    value={form.fiscal_code}
                    onChange={(e) => setForm(f => ({ ...f, fiscal_code: e.target.value }))}
                    placeholder="RSSMRA80A01F205X"
                    maxLength={16}
                  />
                </div>
              )}
              <div className="md:col-span-2">
                <Label htmlFor="cu-addr">Indirizzo</Label>
                <Input
                  id="cu-addr"
                  value={form.address_line}
                  onChange={(e) => setForm(f => ({ ...f, address_line: e.target.value }))}
                  placeholder="Via Roma 12"
                />
              </div>
              <div>
                <Label htmlFor="cu-zip">CAP</Label>
                <Input
                  id="cu-zip"
                  value={form.zip_code}
                  onChange={(e) => setForm(f => ({ ...f, zip_code: e.target.value }))}
                  placeholder="20121"
                />
              </div>
              <div>
                <Label htmlFor="cu-city">Città</Label>
                <Input
                  id="cu-city"
                  value={form.city}
                  onChange={(e) => setForm(f => ({ ...f, city: e.target.value }))}
                  placeholder="Milano"
                />
              </div>
              <div>
                <Label htmlFor="cu-prov">Provincia</Label>
                <Input
                  id="cu-prov"
                  value={form.province}
                  onChange={(e) => setForm(f => ({ ...f, province: e.target.value }))}
                  placeholder="MI"
                  maxLength={2}
                />
              </div>
              <div>
                <Label htmlFor="cu-phone">Telefono</Label>
                <Input
                  id="cu-phone"
                  value={form.phone}
                  onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))}
                  placeholder="+39 02 1234567"
                />
              </div>
              <div>
                <Label htmlFor="cu-email">Email</Label>
                <Input
                  id="cu-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="info@cliente.it"
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="cu-pec">PEC</Label>
                <Input
                  id="cu-pec"
                  value={form.pec}
                  onChange={(e) => setForm(f => ({ ...f, pec: e.target.value }))}
                  placeholder="cliente@pec.it"
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="cu-notes">Note</Label>
                <Textarea
                  id="cu-notes"
                  value={form.notes}
                  onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                  rows={2}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button>
            <Button onClick={handleSubmit} disabled={createCustomer.isPending || updateCustomer.isPending}>
              {form.id ? 'Salva modifiche' : 'Crea cliente'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
          </main>
        </SidebarInset>
      </div>
    </SidebarProvider>
  );
};

export default Customers;
